/**
 * The per-row result of a bulk operation (FS-030).
 *
 * What the four hand-rolled loops this replaces could not show. Each one
 * counted its successes, discarded every exception, and raised a toast saying
 * "37 of 50 deleted" — leaving the user to work out which thirteen, and why,
 * by looking at the list.
 *
 * So the panel leads with the failures and their reasons. A toast is still
 * raised for a clean run, because thirty rows that all worked need no panel.
 */

import { AlertTriangle, Check, X } from "lucide-react";
import type { BulkOutcome } from "@/features/bulk/api";
import Button from "@/components/ui/Button";

interface Props {
  outcome: BulkOutcome | null;
  onClose: () => void;
  /** What the rows are, for the summary line: "machines", "sales orders". */
  noun?: string;
}

function BulkResultPanel({ outcome, onClose, noun = "rows" }: Props) {
  if (!outcome || outcome.failed === 0) return null;

  const failures = outcome.rows.filter((row) => !row.ok);
  const successes = outcome.succeeded;

  return (
    <div
      className="rounded-[var(--radius-lg)] border border-[var(--text-danger)]/30 bg-[var(--surface-raised)] overflow-hidden"
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3 border-b border-[var(--border-default)] bg-[var(--fill-danger)] px-4 py-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[var(--text-danger)]" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-[var(--text-primary)]">
            {failures.length} of {outcome.requested} {noun} could not be done
          </p>
          {successes > 0 && (
            <p className="mt-0.5 text-xs text-[var(--text-secondary)]">
              The other {successes} went through and have been saved.
            </p>
          )}
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={onClose}
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <ul className="max-h-72 divide-y divide-[var(--border-subtle)] overflow-y-auto">
        {failures.map((row) => (
          <li key={row.id} className="flex gap-3 px-4 py-2.5">
            <X className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-danger)]" />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-[var(--text-primary)]">
                {row.label}
              </p>
              <p className="text-xs text-[var(--text-secondary)]">
                {row.error ?? "No reason given."}
              </p>
            </div>
          </li>
        ))}
      </ul>

      {successes > 0 && (
        <div className="flex items-center gap-2 border-t border-[var(--border-subtle)] px-4 py-2 text-xs text-[var(--text-tertiary)]">
          <Check className="h-3.5 w-3.5 text-[var(--text-success)]" />
          {successes} succeeded
        </div>
      )}
    </div>
  );
}

export default BulkResultPanel;
